import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col w-full items-center space-y-6 justify-center" }
const _hoisted_2 = { class: "flex flex-col w-full max-w-xl" }
const _hoisted_3 = { class: "text-lg font-medium text-gray-900 text-left" }
const _hoisted_4 = { class: "p-6 space-y-6" }
const _hoisted_5 = { class: "flex flex-row" }
const _hoisted_6 = { class: "flex flex-row space-x-6" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "px-5 border-l-2 border-gray-200" }, " % ", -1)
const _hoisted_8 = { class: "flex self-end w-1/2 mt-8" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { class: "px-5 border-l-2 border-gray-200" }, " % ", -1)
const _hoisted_10 = { class: "flex flex-row space-x-8" }
const _hoisted_11 = { class: "px-5 border-l-2 border-gray-200" }
const _hoisted_12 = {
  key: 0,
  class: "flex flex-col space-y-2"
}
const _hoisted_13 = {
  key: 0,
  class: "pt-1 mt-1 text-sm text-gray-600 leading-4 border-t border-transparent"
}

import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseCard } from '@/lib/components/Card';
import { BaseSelect } from '@/lib/components/Select';
import { BaseNumberInput } from '@/lib/components/Input';
import { MedKitRadioGroup } from '@bigpicturemedical/medkit';
import HospitalTeamSelect from '../HospitalTeamSelect.vue';
import { IErrors, IOption, IPredefinedTerminologyList } from '@/lib';
import {
  CopdProgramRequest,
  CopdProgramRequestPayload,
  FilterTag,
  CopdProgramSetting,
  TagGroupName
} from '@/models';
import { CopdTerminologyService, FilterTagService } from '@/services/api';
import { predefinedTerminologyListFactory } from '@/lib/helpers/terminology.helper';
import axios from 'axios';
import { FEATURES } from '@/constants';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { capitalize } from '@/lib/helpers/string.helper';
import { usePathwayName } from '@/composables/pathways';

const OXYGEN_DELIVERY_CODE = '722742002';



export default /*#__PURE__*/_defineComponent({
  __name: 'CopdSetup',
  props: {
    program: {},
    pathway: {},
    errors: {}
  },
  emits: ["update-program"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();
const props = __props;
const emit = __emit;

const oxygenTypes = ref<IPredefinedTerminologyList>(predefinedTerminologyListFactory([]));
const inputErrors = ref<IErrors>({});
const fetchErrors = ref<IErrors | null>(null);
const selectedHomeMonitoringOption = ref<FilterTag['id']>('');
const homeMonitoringOptions = ref<Array<FilterTag>>([]);
const hospitalTeams = ref<FilterTag[]>([]);
const tagService = new FilterTagService();
const copdTerminologyService = new CopdTerminologyService();

const tagsFeatureFlag = computed<boolean>(() => isFeatureFlagEnabled(FEATURES.TAGS));
const validationErrors = computed(() => inputErrors.value || fetchErrors.value);
const homeMonitoringValidationError = computed(() => validationErrors.value?.home_monitoring?.[0]);
const inspiredOxygenLabel = computed<string>(() => capitalize(t('custom.uhb.copd.inspired-oxygen')));
const inspiredOxygenPlaceholder = computed<string>(() => capitalize(
  t(
    'custom.uhb.copd.select-entity',
    [t('custom.uhb.copd.inspired-oxygen')]
  )
));
const oxygenValue = computed<string>(() => props.program.settings.method_of_oxygen_delivery ? props.program.settings.method_of_oxygen_delivery.code : '');
const flowRateLabel = computed<string>(() => capitalize(t('custom.uhb.copd.flow-rate')));
const showFlowRate = computed<boolean>(() => {
  // TODO : return this value from BE
  const roomAirCode = OXYGEN_DELIVERY_CODE;
  const oxygenMethod = oxygenTypes.value.find(oxygenValue.value);
  return !!oxygenMethod && oxygenMethod.code !== roomAirCode;
});
const homeMonitoringValues = computed<Array<IOption>>(() => homeMonitoringOptions.value.map((option: FilterTag) => ({
  value: option.id,
  label: option.name
})));

// Constant used until value start coming from BE
watch(() => props.errors, (newVal) => {
  inputErrors.value = newVal;
});
onMounted(() => {
  inputErrors.value = props.errors;
  fetchOxygenTypes();
  fetchHospitalTeams();
});
onBeforeMount(() => {
  if (tagsFeatureFlag.value) {
    fetchHomeMonitoringOptions();
  }
});
const updateSettings = (newVal: Partial<CopdProgramSetting>) => {
  const updated = {
    ...props.program,
    settings: {
      ...props.program.settings,
      ...newVal
    }
  };

  if (updated.settings.method_of_oxygen_delivery.code === OXYGEN_DELIVERY_CODE) {
    delete updated.settings.flow_rate;
  }

  exposed.triggerUpdateProgram(updated);

  Object.keys(newVal).forEach((updatedField) => {
    inputErrors.value?.[`settings.${updatedField}`] && delete inputErrors.value[`settings.${updatedField}`];
  });
};
const updateProgram = (updated: CopdProgramRequestPayload): void => {
  const updatedProgram = {
    ...props.program,
    ...updated
  };

  exposed.triggerUpdateProgram(updatedProgram);

  Object.keys(updated).forEach((updatedField) => {
    inputErrors.value?.[`${updatedField}`] && delete inputErrors.value[`${updatedField}`];
  });
};
const triggerUpdateProgram = (updated: CopdProgramRequest): void => {
  emit('update-program', updated);
};
const fetchOxygenTypes = async () => {
  try {
    const response = await copdTerminologyService.getMethodOfOxygenDelivery();
    oxygenTypes.value = predefinedTerminologyListFactory(response.data);
  } catch (e) {
    if (!axios.isAxiosError(e)) {
      throw e;
    }
    if (e.response.status === 422) {
      fetchErrors.value = e.response.data?.errors;
    } else {
      fetchErrors.value = {};
    }
  }
};
const fetchHomeMonitoringOptions = async () => {
  try {
    const response = await tagService.getTagsForGroupName(TagGroupName.HomeMonitoring);
    homeMonitoringOptions.value = response.sort((a: FilterTag, b: FilterTag): number => b.name.localeCompare(a.name));
  } catch (e) {
    if (!axios.isAxiosError(e)) {
      throw e;
    }
    if (e.response.status === 422) {
      fetchErrors.value = e.response.data?.errors;
    } else {
      fetchErrors.value = {};
    }
  }
};

const fetchHospitalTeams = async () => {
  try {
    hospitalTeams.value = await tagService.getTagsForGroupName(TagGroupName.HospitalTeam);
  } catch (e) {
    fetchErrors.value = {};
    if (!axios.isAxiosError(e)) {
      throw e;
    }
    if (e.response.status === 422) {
      fetchErrors.value = e.response.data?.errors;
    }
  }
};

const updateHomeMonitoringOption = (option: FilterTag['id']): void => {
  selectedHomeMonitoringOption.value = option;
  exposed.updateProgram({ home_monitoring: option });
};

const exposed = {
  triggerUpdateProgram,
  updateProgram
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)('custom.uhb.pathways.add-patient-to', { pathwayName: _unref(usePathwayName)(props.pathway) })), 1)
    ]),
    _createVNode(_unref(BaseCard), {
      class: "flex flex-col w-full max-w-xl",
      padding: "0"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            (tagsFeatureFlag.value)
              ? (_openBlock(), _createBlock(HospitalTeamSelect, {
                  key: 0,
                  "hospital-team": _ctx.program.hospital_team,
                  "hospital-teams": hospitalTeams.value,
                  error: validationErrors.value?.hospital_team?.[0],
                  "onUpdate:hospitalTeam": _cache[0] || (_cache[0] = ($event: any) => (updateProgram({ hospital_team: $event.id })))
                }, null, 8, ["hospital-team", "hospital-teams", "error"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(BaseNumberInput), {
              "model-value": `${_ctx.program.settings.spo2_range_low}`,
              "data-dusk": "copd-settings-spo2-min-target",
              class: "w-1/2",
              step: "any",
              "show-button": false,
              label: _ctx.$t('custom.uhb.copd.spo2-target-range'),
              placeholder: "Min",
              error: validationErrors.value?.['settings.spo2_range_low']?.[0],
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (updateSettings({ spo2_range_low: $event === '' ? null : +$event })))
            }, {
              "use-default": _withCtx(() => [
                _hoisted_7
              ]),
              _: 1
            }, 8, ["model-value", "label", "error"]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(BaseNumberInput), {
                "model-value": `${_ctx.program.settings.spo2_range_high}`,
                "data-dusk": "copd-settings-spo2-max-target",
                "show-button": false,
                step: "any",
                placeholder: "Max",
                error: validationErrors.value?.['settings.spo2_range_high']?.[0],
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (updateSettings({ spo2_range_high: $event === '' ? null : +$event })))
              }, {
                "use-default": _withCtx(() => [
                  _hoisted_9
                ]),
                _: 1
              }, 8, ["model-value", "error"])
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_unref(BaseSelect), {
              "data-dusk": "copd-settings-inspired-oxygen",
              "model-value": oxygenValue.value,
              class: _normalizeClass(showFlowRate.value ? 'w-1/2' : 'w-full'),
              label: inspiredOxygenLabel.value,
              placeholder: inspiredOxygenPlaceholder.value,
              options: oxygenTypes.value && oxygenTypes.value.toOptions(),
              error: validationErrors.value?.['settings.method_of_oxygen_delivery']?.[0],
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (updateSettings({ method_of_oxygen_delivery: oxygenTypes.value.find($event as string) })))
            }, null, 8, ["model-value", "class", "label", "placeholder", "options", "error"]),
            (showFlowRate.value)
              ? (_openBlock(), _createBlock(_unref(BaseNumberInput), {
                  key: 0,
                  "model-value": _ctx.program.settings.flow_rate,
                  "data-dusk": "copd-settings-flow-rate",
                  class: "w-1/2",
                  step: "any",
                  min: "0",
                  max: "15",
                  "show-button": false,
                  label: flowRateLabel.value,
                  error: validationErrors.value?.['settings.flow_rate']?.[0],
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (updateSettings({ flow_rate: $event })))
                }, {
                  "use-default": _withCtx(() => [
                    _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.$t('custom.uhb.copd.litres-per-min')), 1)
                  ]),
                  _: 1
                }, 8, ["model-value", "label", "error"]))
              : _createCommentVNode("", true)
          ]),
          (tagsFeatureFlag.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createVNode(_unref(MedKitRadioGroup), {
                  name: "is-the-patient-home-monitoring",
                  label: _ctx.$t('custom.uhb.copd.is-the-patient-home-monitoring'),
                  options: homeMonitoringValues.value,
                  "model-value": selectedHomeMonitoringOption.value,
                  error: homeMonitoringValidationError.value,
                  required: "",
                  "data-dusk": "home-monitoring-options",
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (updateHomeMonitoringOption($event)))
                }, null, 8, ["label", "options", "model-value", "error"]),
                (!homeMonitoringValidationError.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_ctx.$t('custom.uhb.copd.where-patient-will-record-at-home')), 1))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    })
  ]))
}
}

})